/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ListGroup from "react-bootstrap/ListGroup";

import { FaPencilAlt, FaTooth, FaCopy } from "react-icons/fa";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

/* ============================================================
   RESPONSIVE HOOK
   ============================================================ */

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(query);

    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = () => setMatches(media.matches);

    if (media.addEventListener) {
      media.addEventListener("change", listener);
    } else {
      media.addListener(listener);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", listener);
      } else {
        media.removeListener(listener);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return matches;
};

/* ============================================================
   CUSTOM DROPDOWN
   ============================================================ */

const CustomDropdown = ({
  value,
  options,
  onSelect,
  placeholder = "Select...",
  disabled = false,
  fontSize = "1rem",
  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = React.useRef(null);

  const selected = options.find((opt) => opt.value === value);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (e) => {
      if (
        e.target.closest("[data-custom-select-menu]") ||
        e.target.closest("[data-custom-select-trigger]")
      ) {
        return;
      }
      setOpen(false);
    };

    const handleReposition = () => setOpen(false);

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) return;

    if (open) {
      setOpen(false);
      return;
    }

    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const menuHeightEstimate = Math.min(240, options.length * 36 + 8);

    let top = rect.bottom + 4;

    if (top + menuHeightEstimate > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - menuHeightEstimate - 4);
    }

    let left = rect.left;

    if (left + rect.width > window.innerWidth - margin) {
      left = window.innerWidth - rect.width - margin;
    }

    left = Math.max(margin, left);

    setPos({ top, left, width: rect.width });
    setOpen(true);
  };

  return (
    <>
      <div
        data-custom-select-trigger
        onClick={toggle}
        ref={triggerRef}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          width: "100%",
          minHeight: "38px",
          padding: "6px 12px",
          borderColor: "#f4f4f4",
          boxShadow: "0 0 5px rgba(0, 74, 173, 0.3)",
          borderRadius: "8px",
          border: "1px solid #f4f4f4",
          backgroundColor: disabled ? "#e9ecef" : "#fff",
          color: "#333",
          fontSize,
          cursor: disabled ? "not-allowed" : "pointer",
          userSelect: "none",
          overflow: "hidden",
          boxSizing: "border-box",
          minWidth: 0,
          ...style,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          {selected ? selected.label : placeholder}
        </span>

        <span style={{ color: "#666", flexShrink: 0, fontSize: "0.8em" }}>
          ▾
        </span>
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            data-custom-select-menu
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxHeight: "240px",
              overflowY: "auto",
              backgroundColor: "white",
              border: "1px solid #ced4da",
              borderRadius: "6px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              zIndex: 3000,
              boxSizing: "border-box",
            }}
          >
            {options.map((opt, idx) => (
              <div
                key={`${opt.value}-${idx}`}
                onClick={() => {
                  onSelect(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize,
                  backgroundColor:
                    opt.value === value ? "#f1f5ff" : "transparent",
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f1f1f1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    opt.value === value ? "#f1f5ff" : "transparent";
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

/* ============================================================
   EXISTING STYLES
   ============================================================ */

const modalBodyStyle = {
  padding: "20px",
};

const toothButtonStyle = {
  width: "30px",
  height: "40px",
  margin: "2px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #ccc",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "14px",
  padding: 0,
};

const selectedToothStyle = {
  ...toothButtonStyle,
  backgroundColor: "#004aad",
  color: "white",
  borderColor: "#004aad",
};

const requiredLabelStyle = {
  display: "block",
  marginBottom: "5px",
  fontWeight: "bold",
  color: "#000000",
};

/* ============================================================
   FIELD ROW (desktop: label left / input right, mobile: stacked)
   ============================================================ */

const FieldRow = ({ label, required, isMobile, fontSize, children }) => {
  const labelContent = (
    <>
      {label}
      {required && (
        <>
          {" "}
          <span style={{ color: "red" }}>*</span>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        <Form.Label style={{ ...requiredLabelStyle, fontSize }}>
          {labelContent}
        </Form.Label>
        {children}
      </>
    );
  }

  return (
    <Row className="mx-0 align-items-start">
      <Col
        sm={2}
        style={{ paddingLeft: 0, paddingRight: "8px", paddingTop: "8px" }}
      >
        <Form.Label
          style={{
            ...requiredLabelStyle,
            marginBottom: 0,
            fontSize,
            color: required ? "#000000" : "#333",
          }}
        >
          {labelContent}
        </Form.Label>
      </Col>

      <Col sm={10} style={{ paddingLeft: 0, paddingRight: 0 }}>
        {children}
      </Col>
    </Row>
  );
};

/* ============================================================
   PROCEDURE SELECTOR MODAL
   ============================================================ */

const ProcedureSelectorModal = ({
  show,
  handleClose,
  onSelect,
  allProcedures,
  accountId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const isMobile = useMediaQuery("(max-width: 767px)");

  const fs = (desktop, mobile) => (isMobile ? mobile : desktop);

  const frequentlyUsedProcedureIds = ["1093", "1035"];

  const filteredProcedures = useMemo(() => {
    if (!searchTerm) {
      return allProcedures.filter(
        (proc) => !accountId || proc.accountId === accountId
      );
    }

    return allProcedures.filter(
      (proc) =>
        proc.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!accountId || proc.accountId === accountId)
    );
  }, [searchTerm, allProcedures, accountId]);

  const frequentlyUsed = filteredProcedures.filter((p) =>
    frequentlyUsedProcedureIds.includes(p.procedureId)
  );

  const generalProcedures = filteredProcedures.filter(
    (p) => !frequentlyUsedProcedureIds.includes(p.procedureId) && p.value
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          backgroundColor: "#004aad",
          color: "white",
          borderBottom: "none",
        }}
      >
        <Modal.Title style={{ fontSize: fs("1.25rem", "1rem") }}>
          Select Procedure
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "1rem", borderBottom: "1px solid #dee2e6" }}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: fs("1rem", "0.85rem") }}
          />
        </div>

        <div
          style={{
            maxHeight: fs("60vh", "50vh"),
            overflowY: "auto",
            padding: "1rem",
          }}
        >
          {frequentlyUsed.length > 0 && (
            <>
              <h5 style={{ fontSize: fs("1.25rem", "1rem") }}>
                Frequently Used
              </h5>

              <ListGroup variant="flush">
                {frequentlyUsed.map((proc) => (
                  <ListGroup.Item
                    key={`freq-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                      fontSize: fs("1rem", "0.85rem"),
                    }}
                  >
                    {proc.label} (${proc.price || 0})
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <hr />
            </>
          )}

          {generalProcedures.length > 0 && (
            <>
              <h5 style={{ fontSize: fs("1.25rem", "1rem") }}>
                General Procedures
              </h5>

              <ListGroup variant="flush">
                {generalProcedures.map((proc) => (
                  <ListGroup.Item
                    key={`gen-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                      fontSize: fs("1rem", "0.85rem"),
                    }}
                  >
                    {proc.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}

          {frequentlyUsed.length === 0 && generalProcedures.length === 0 && (
            <p style={{ fontSize: fs("1rem", "0.85rem") }}>
              No procedures available for this account.
            </p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

/* ============================================================
   TOOTH SELECTOR MODAL
   ============================================================ */

const ToothSelectorModal = ({ show, handleClose, currentTeeth, onSave }) => {
  const [selectedTeeth, setSelectedTeeth] = useState([]);

  const isMobile = useMediaQuery("(max-width: 767px)");

  const fs = (desktop, mobile) => (isMobile ? mobile : desktop);

  useEffect(() => {
    if (currentTeeth) {
      setSelectedTeeth(
        currentTeeth
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t)
      );
    }
  }, [currentTeeth]);

  const toggleTooth = (toothNumber) => {
    setSelectedTeeth((prevSelected) =>
      prevSelected.includes(toothNumber.toString())
        ? prevSelected.filter((t) => t !== toothNumber.toString())
        : [...prevSelected, toothNumber.toString()]
    );
  };

  const toggleGroup = (teethArray) => {
    const teeth = teethArray.map((tooth) => tooth.toString());

    setSelectedTeeth((prevSelected) => {
      const allSelected = teeth.every((tooth) => prevSelected.includes(tooth));

      if (allSelected) {
        return prevSelected.filter((t) => !teeth.includes(t));
      }

      const newSelection = [...prevSelected];

      teeth.forEach((tooth) => {
        if (!newSelection.includes(tooth)) {
          newSelection.push(tooth);
        }
      });

      return newSelection;
    });
  };

  const handleDone = () => {
    onSave(
      selectedTeeth.sort((a, b) => parseInt(a) - parseInt(b)).join(", ")
    );

    handleClose();
  };

  const mobileToothButtonStyle = {
    width: "25px",
    height: "32px",
    margin: "1px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #ccc",
    borderRadius: "3px",
    fontWeight: "bold",
    fontSize: "10px",
    padding: 0,
    flexShrink: 0,
    backgroundColor: "white",
  };

  const mobileSelectedToothStyle = {
    ...mobileToothButtonStyle,
    backgroundColor: "#004aad",
    color: "white",
    borderColor: "#004aad",
  };

  const renderToothButton = (toothNumber) => (
    <button
      type="button"
      key={toothNumber}
      onClick={() => toggleTooth(toothNumber)}
      style={
        isMobile
          ? selectedTeeth.includes(toothNumber.toString())
            ? mobileSelectedToothStyle
            : mobileToothButtonStyle
          : selectedTeeth.includes(toothNumber.toString())
          ? selectedToothStyle
          : toothButtonStyle
      }
    >
      {toothNumber}
    </button>
  );

  const teethLayout = {
    upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
    upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
    lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
    lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
  };

  const headCellStyle = {
    cursor: "pointer",
    fontSize: fs("1rem", "0.7rem"),
    padding: isMobile ? "4px" : undefined,
  };

  const cellStyle = { padding: isMobile ? "1px" : "2px" };

  const tableStyle = {
    minWidth: isMobile ? "475px" : "auto",
    width: isMobile ? "475px" : "100%",
    marginBottom: "0",
    fontSize: fs("1rem", "0.7rem"),
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName={isMobile ? "tooth-selector-mobile-modal" : undefined}
      contentClassName={isMobile ? "tooth-selector-mobile-content" : undefined}
    >
      <Modal.Header
        closeButton
        style={{
          backgroundColor: "#004aad",
          color: "white",
          padding: isMobile ? "8px 12px" : undefined,
        }}
      >
        <Modal.Title
          style={{ fontSize: fs("1.25rem", "0.95rem"), lineHeight: "1.2" }}
        >
          Tooth Selector
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          ...modalBodyStyle,
          maxHeight: isMobile ? "calc(100vh - 125px)" : "70vh",
          overflowY: "auto",
          overflowX: "hidden",
          padding: isMobile ? "10px" : modalBodyStyle.padding,
          boxSizing: "border-box",
        }}
      >
        {/* UPPER ARCH */}

        <div
          className="text-center"
          style={{ marginBottom: isMobile ? "8px" : "16px" }}
        >
          <h5
            style={{
              cursor: "pointer",
              display: "inline-block",
              fontSize: fs("1.1rem", "0.85rem"),
              marginBottom: 0,
            }}
            onClick={() =>
              toggleGroup([...teethLayout.upperRight, ...teethLayout.upperLeft])
            }
          >
            Upper Arch
          </h5>
        </div>

        <div
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            width: "100%",
            WebkitOverflowScrolling: "touch",
            marginBottom: isMobile ? "10px" : "24px",
          }}
        >
          <table
            className="table table-bordered text-center mb-2"
            style={tableStyle}
          >
            <thead>
              <tr>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={headCellStyle}
                  onClick={() => toggleGroup(teethLayout.upperRight)}
                >
                  Upper Right
                </th>

                <th
                  colSpan="8"
                  className="bg-light"
                  style={headCellStyle}
                  onClick={() => toggleGroup(teethLayout.upperLeft)}
                >
                  Upper Left
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                {teethLayout.upperRight.map((tooth) => (
                  <td key={tooth} style={cellStyle}>
                    {renderToothButton(tooth)}
                  </td>
                ))}

                {teethLayout.upperLeft.map((tooth) => (
                  <td key={tooth} style={cellStyle}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <hr style={{ margin: isMobile ? "8px 0" : undefined }} />

        {/* LOWER ARCH */}

        <div
          className="text-center"
          style={{ marginBottom: isMobile ? "8px" : "16px" }}
        >
          <h5
            style={{
              cursor: "pointer",
              display: "inline-block",
              fontSize: fs("1.1rem", "0.85rem"),
              marginBottom: 0,
            }}
            onClick={() =>
              toggleGroup([...teethLayout.lowerRight, ...teethLayout.lowerLeft])
            }
          >
            Lower Arch
          </h5>
        </div>

        <div
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            width: "100%",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table
            className="table table-bordered text-center mb-2"
            style={tableStyle}
          >
            <thead>
              <tr>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={headCellStyle}
                  onClick={() => toggleGroup(teethLayout.lowerRight)}
                >
                  Lower Right
                </th>

                <th
                  colSpan="8"
                  className="bg-light"
                  style={headCellStyle}
                  onClick={() => toggleGroup(teethLayout.lowerLeft)}
                >
                  Lower Left
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                {teethLayout.lowerRight.map((tooth) => (
                  <td key={tooth} style={cellStyle}>
                    {renderToothButton(tooth)}
                  </td>
                ))}

                {teethLayout.lowerLeft.map((tooth) => (
                  <td key={tooth} style={cellStyle}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer
        style={{
          padding: isMobile ? "7px 10px" : undefined,
          gap: isMobile ? "5px" : undefined,
        }}
      >
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{
            fontSize: fs("1rem", "0.75rem"),
            padding: isMobile ? "5px 10px" : undefined,
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleDone}
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
            fontSize: fs("1rem", "0.75rem"),
            padding: isMobile ? "5px 10px" : undefined,
          }}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ============================================================
   GET USER LAB ID
   ============================================================ */

const getUserLabId = () => {
  const userLabId =
    localStorage.getItem("userLabId") || localStorage.getItem("labId");

  console.log("Retrieved userLabId from localStorage:", userLabId);

  return userLabId;
};

/* ============================================================
   PROCEDURE MODEL
   ============================================================ */

const EMPTY_PROCEDURE = {
  label: "Select Procedure",
  value: "",
  price: 0,
  procedureId: "",
  accountId: "",
  unitCount: "Count by Teeth",
};

const ProcedureModel = ({ onProcedureAdded, account }) => {
  const { caseId } = useParams();

  const isMobile = useMediaQuery("(max-width: 767px)");

  const fs = (desktop, mobile) => (isMobile ? mobile : desktop);

  const [show, setShow] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(EMPTY_PROCEDURE);
  const [teeth, setTeeth] = useState("");
  const [shade, setShade] = useState("");
  const [alloyMaterial, setAlloyMaterial] = useState("");
  const [contactsEmbrasures, setContactsEmbrasures] = useState("");
  const [occlusalContact, setOcclusalContact] = useState("");
  const [insufficientRoom, setInsufficientRoom] = useState("");
  const [retention, setRetention] = useState("");
  const [marginPosition, setMarginPosition] = useState("");
  const [showToothSelectorModal, setShowToothSelectorModal] = useState(false);
  const [showProcedureSelectorModal, setShowProcedureSelectorModal] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [isLoadingProcedures, setIsLoadingProcedures] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [caseAccountId, setCaseAccountId] = useState(null);

  /* ==========================================================
     FETCH CASE DETAILS
     ========================================================== */

  useEffect(() => {
    const fetchCaseDetails = async () => {
      if (!caseId) {
        console.error("No case ID provided");
        toast.error("No case ID provided");
        setIsLoadingProcedures(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`,
          { withCredentials: true }
        );

        const caseData = response.data;

        setCaseAccountId(caseData.account_holder || account || null);
      } catch (error) {
        console.error(
          "Error fetching case details:",
          error.response?.data || error.message
        );

        setFetchError(`Failed to fetch case details: ${error.message}`);

        toast.error(`Failed to fetch case details: ${error.message}`);

        setCaseAccountId(account || null);
      }
    };

    fetchCaseDetails();
  }, [caseId, account]);

  /* ==========================================================
     FETCH PROCEDURES
     ========================================================== */

  useEffect(() => {
    const fetchProcedures = async () => {
      if (!caseAccountId) {
        setIsLoadingProcedures(false);
        return;
      }

      const userLabId = getUserLabId();

      if (!userLabId) {
        console.error("No userLabId found in localStorage");

        setFetchError("User Lab ID not found. Please login again.");

        setIsLoadingProcedures(false);

        toast.error("User Lab ID not found. Please login again.");

        return;
      }

      try {
        setIsLoadingProcedures(true);
        setFetchError(null);

        const url = `https://hms.backend.hiragroupcloud.com/api/procedures/account-specific?accountId=${caseAccountId}&userLabId=${userLabId}`;

        console.log("Fetching procedures from URL:", url);

        const response = await axios.get(url, { withCredentials: true });

        console.log("API Response:", response.data);

        const data = response.data.data || [];

        const formattedProcedures = data.map((proc) => ({
          label: `${proc.name} (ID: ${proc.procedureId || "N/A"})`,
          value: proc._id,
          price: proc.price || 0,
          procedureId: proc.procedureId || "",
          accountId: proc.accountId || caseAccountId,
          unitCount: proc.unitCount || "Count by Teeth",
        }));

        setProcedureOptions([
          {
            label: "Select a procedure...",
            value: "",
            price: 0,
            procedureId: "",
            accountId: "",
            unitCount: "Count by Teeth",
          },
          ...formattedProcedures,
        ]);

        if (formattedProcedures.length === 0) {
          toast.warn("No procedures found for this account");
        }
      } catch (error) {
        console.error("Error fetching procedures:", error);
        console.error("Error response:", error.response?.data);

        setFetchError(
          `Failed to load procedures: ${
            error.response?.data?.message || error.message
          }`
        );

        toast.error(
          `Failed to load procedures: ${
            error.response?.data?.message || error.message
          }`
        );

        setProcedureOptions([
          {
            label: "Select a procedure...",
            value: "",
            price: 0,
            procedureId: "",
            accountId: "",
            unitCount: "Count by Teeth",
          },
        ]);
      } finally {
        setIsLoadingProcedures(false);
      }
    };

    if (show && caseAccountId) {
      fetchProcedures();
    }
  }, [caseAccountId, show]);

  /* ==========================================================
     RESET / CLOSE
     ========================================================== */

  const resetAllProcedureFields = () => {
    setSelectedProcedure(EMPTY_PROCEDURE);
    setTeeth("");
    setShade("");
    setAlloyMaterial("");
    setContactsEmbrasures("");
    setOcclusalContact("");
    setInsufficientRoom("");
    setRetention("");
    setMarginPosition("");
  };

  const handleClose = () => {
    setShow(false);
    resetAllProcedureFields();
  };

  /* ==========================================================
     PROCEDURE SELECT
     ========================================================== */

  const handleProcedureSelect = (procedureId) => {
    const selectedProc = procedureOptions.find(
      (proc) => proc.value === procedureId
    );

    if (selectedProc) {
      setSelectedProcedure({
        label: selectedProc.label,
        value: selectedProc.value,
        price: selectedProc.price || 0,
        procedureId: selectedProc.procedureId,
        accountId: selectedProc.accountId,
        unitCount: selectedProc.unitCount || "Count by Teeth",
      });
    } else {
      setSelectedProcedure(EMPTY_PROCEDURE);
    }

    setShowProcedureSelectorModal(false);
  };

  /* ==========================================================
     SAVE TEETH
     ========================================================== */

  const handleSaveTeeth = (selectedTeethValue) => {
    setTeeth(selectedTeethValue);
    setShowToothSelectorModal(false);
  };

  /* ==========================================================
     SAVE PROCEDURE
     ========================================================== */

  const handleSaveProcedure = async () => {
    if (
      !caseId ||
      !selectedProcedure.value ||
      !teeth?.trim() ||
      !shade?.trim()
    ) {
      toast.error("Procedure, teeth, and shade are required");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        customProcedureId: selectedProcedure.procedureId,
        teeth: teeth.trim(),
        shade: shade.trim(),
        alloyMaterial: alloyMaterial || "",
        contactsEmbrasures: contactsEmbrasures || "",
        occlusalContact: occlusalContact || "",
        insufficientRoom: insufficientRoom || "",
        retention: retention || "",
        marginPosition: marginPosition || "",
        price: selectedProcedure.price,
        unitCount: selectedProcedure.unitCount,
      };

      console.log("Saving procedure with payload:", payload);

      const response = await axios.post(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/procedures`,
        payload,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Procedure added successfully!");

        resetAllProcedureFields();

        handleClose();

        if (onProcedureAdded) {
          await onProcedureAdded();
        }
      } else {
        toast.error(response.data.message || "Failed to add procedure");
      }
    } catch (error) {
      console.error("Error saving procedure:", error);

      toast.error(error.response?.data?.message || "Failed to add procedure");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ==========================================================
     OPTIONS
     ========================================================== */

  const alloyOptions = ["", "Zirconia", "Emax", "PFM", "Gold", "Composite"];

  const contactOptions = ["", "Normal", "Broad", "Open", "Closed", "Light"];

  const occlusalOptions = [
    "",
    "In Occlusion",
    "Out of Occlusion",
    "Light Contact",
  ];

  const roomOptions = ["", "Yes", "No", "Minimal Prep Required"];

  const retentionOptions = [
    "",
    "Good",
    "Fair",
    "Poor",
    "Mechanical",
    "Adhesive",
  ];

  const marginOptions = ["", "Supragingival", "Equigingival", "Subgingival"];

  const otherFields = [
    {
      id: "formAlloyMaterial",
      label: "Alloy / Material",
      value: alloyMaterial,
      set: setAlloyMaterial,
      options: alloyOptions,
    },
    {
      id: "formContacts",
      label: "Contacts / Embrasures",
      value: contactsEmbrasures,
      set: setContactsEmbrasures,
      options: contactOptions,
    },
    {
      id: "formOcclusal",
      label: "Occlusal Contact",
      value: occlusalContact,
      set: setOcclusalContact,
      options: occlusalOptions,
    },
    {
      id: "formRoom",
      label: "Insufficient Room",
      value: insufficientRoom,
      set: setInsufficientRoom,
      options: roomOptions,
    },
    {
      id: "formRetention",
      label: "Retention",
      value: retention,
      set: setRetention,
      options: retentionOptions,
    },
    {
      id: "formMargin",
      label: "Margin Position",
      value: marginPosition,
      set: setMarginPosition,
      options: marginOptions,
    },
  ];

  const labelFont = fs("1rem", "0.85rem");

  /* ==========================================================
     RETURN
     ========================================================== */

  return (
    <>
      {/* MOBILE CSS ONLY */}

      <style>
        {`
          @media (max-width: 767px) {

            .procedure-mobile-modal .modal-title {
              font-size: 1rem !important;
            }

            .procedure-mobile-modal .modal-body {
              padding: 12px !important;
            }

            .procedure-mobile-modal .form-label {
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
              flex: 0 0 100% !important;
              text-align: left !important;
              padding: 0 !important;
              margin-bottom: 4px !important;
            }

            .procedure-mobile-modal .form-row > .col,
            .procedure-mobile-modal .form-row > [class*="col-"] {
              flex: 0 0 100% !important;
              max-width: 100% !important;
              width: 100% !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }

            .procedure-mobile-modal .row {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }

            .tooth-selector-mobile-modal {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 6px !important;
            }

            .tooth-selector-mobile-modal
            .modal-content {
              width: 100% !important;
              max-width: 100% !important;
              max-height: calc(100vh - 12px) !important;
              overflow: hidden !important;
              border-radius: 8px !important;
            }

            .tooth-selector-mobile-content {
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }

            .tooth-selector-mobile-modal
            .modal-body {
              overflow-x: hidden !important;
            }

            .tooth-selector-mobile-modal
            .modal-footer {
              flex-shrink: 0 !important;
            }

            .tooth-selector-mobile-modal
            .modal-header {
              flex-shrink: 0 !important;
            }
          }

          @media (max-width: 400px) {

            .tooth-selector-mobile-modal {
              padding: 4px !important;
            }

            .tooth-selector-mobile-modal
            .modal-title {
              font-size: 0.9rem !important;
            }
          }
        `}
      </style>

      {/* ADD PROCEDURE BUTTON */}

      <Dropdown as={ButtonGroup}>
        <Button
          style={{
            backgroundColor: "white",
            borderColor: "white",
            color: "black",
          }}
          onClick={() => setShow(true)}
        >
          <FaPencilAlt style={{ fontSize: "1.2rem", color: "black" }} /> Add
          Procedure
        </Button>

        <Dropdown.Toggle
          split
          style={{
            backgroundColor: "white",
            borderColor: "white",
            color: "black",
          }}
          id="dropdown-split-basic"
        />

        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() =>
              toast.error("Delete Selected Procedure action not implemented")
            }
          >
            Delete Selected Procedure
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* MAIN MODAL */}

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        dialogClassName={
          isMobile ? "procedure-mobile-modal custom-modal" : "custom-modal"
        }
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
          }}
        >
          <Modal.Title
            className="heading"
            style={{ fontSize: fs("1.5rem", "1.1rem") }}
          >
            Add Procedure
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            overflowY: "auto",
            height: isMobile ? "auto" : "min(600px, calc(100vh - 200px))",
            minHeight: isMobile ? undefined : "420px",
            maxHeight: fs("none", "75vh"),
          }}
        >
          <Form>
            {/* PROCEDURE (label on top, select + copy button below) */}

            <Form.Group
              as={Col}
              className="mb-3"
              controlId="formWork"
              style={{ paddingLeft: 0, paddingRight: 0 }}
            >
              <Form.Label
                style={{ ...requiredLabelStyle, fontSize: labelFont }}
              >
                Procedure <span style={{ color: "red" }}>*</span>
              </Form.Label>

              <Col
                sm={10}
                style={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  width: "100%",
                  maxWidth: "100%",
                  flex: "0 0 100%",
                }}
              >
                {isLoadingProcedures ? (
                  <div className="text-muted" style={{ fontSize: labelFont }}>
                    Loading procedures...
                  </div>
                ) : fetchError ? (
                  <div className="text-danger" style={{ fontSize: labelFont }}>
                    Error: {fetchError}
                  </div>
                ) : procedureOptions.length <= 1 ? (
                  <div className="text-muted" style={{ fontSize: labelFont }}>
                    No procedures available for account ID:{" "}
                    {caseAccountId || "N/A"}
                  </div>
                ) : (
                  <InputGroup
                    style={{
                      width: isMobile ? "100%" : "83.3333%",
                      marginLeft: isMobile ? "0" : "16.6667%",
                      flexWrap: "nowrap",
                    }}
                  >
                    <CustomDropdown
                      value={selectedProcedure.value}
                      onSelect={(val) => handleProcedureSelect(val)}
                      disabled={isLoadingProcedures}
                      fontSize={labelFont}
                      options={procedureOptions.map((option) => ({
                        value: option.value,
                        label: option.label.replace(/\(ID:.*\)/, "").trim(),
                      }))}
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    />

                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowProcedureSelectorModal(true)}
                      style={{
                        backgroundColor: "#004aad",
                        borderColor: "#004aad",
                        color: "#fff",
                        flexShrink: 0,
                      }}
                      disabled={
                        isLoadingProcedures || procedureOptions.length <= 1
                      }
                    >
                      <FaCopy style={{ color: "#fff" }} />
                    </Button>
                  </InputGroup>
                )}
              </Col>
            </Form.Group>

            {/* REMAINING FIELDS (after a procedure is selected) */}

            {selectedProcedure.value && (
              <>
                {/* TEETH */}

                <Form.Group className="mb-3" controlId="formTeeth">
                  <FieldRow
                    label="Teeth"
                    required
                    isMobile={isMobile}
                    fontSize={labelFont}
                  >
                    <InputGroup style={{ flexWrap: "nowrap" }}>
                      <Form.Control
                        type="text"
                        placeholder="Select Teeth"
                        value={teeth}
                        readOnly
                        onClick={() => setShowToothSelectorModal(true)}
                        style={{
                          cursor: "pointer",
                          borderColor: "#f4f4f4",
                          boxShadow: "0 0 5px rgba(0, 74, 173, 0.3)",
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                          borderTopRightRadius: "0",
                          borderBottomRightRadius: "0",
                          fontSize: labelFont,
                        }}
                        required
                      />

                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowToothSelectorModal(true)}
                        style={{
                          backgroundColor: "#004aad",
                          borderColor: "#004aad",
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                          borderTopLeftRadius: "0",
                          borderBottomLeftRadius: "0",
                        }}
                      >
                        <FaTooth style={{ color: "white" }} />
                      </Button>
                    </InputGroup>
                  </FieldRow>
                </Form.Group>

                {/* SHADE */}

                <Form.Group className="mb-3" controlId="formShade">
                  <FieldRow
                    label="Shade"
                    required
                    isMobile={isMobile}
                    fontSize={labelFont}
                  >
                    <Form.Control
                      type="text"
                      value={shade}
                      onChange={(e) => setShade(e.target.value)}
                      placeholder="Enter shade"
                      required
                      style={{
                        borderColor: "#f4f4f4",
                        boxShadow: "0 0 5px rgba(0, 74, 173, 0.3)",
                        borderRadius: "8px",
                        fontSize: labelFont,
                      }}
                    />
                  </FieldRow>
                </Form.Group>

                {/* OTHER FIELDS */}

                {otherFields.map((field) => (
                  <Form.Group
                    className="mb-3"
                    controlId={field.id}
                    key={field.id}
                  >
                    <FieldRow
                      label={field.label}
                      isMobile={isMobile}
                      fontSize={labelFont}
                    >
                      <CustomDropdown
                        value={field.value}
                        onSelect={(val) => field.set(val)}
                        fontSize={labelFont}
                        options={field.options.map((opt) => ({
                          value: opt,
                          label: opt || "—",
                        }))}
                      />
                    </FieldRow>
                  </Form.Group>
                ))}
              </>
            )}
          </Form>
        </Modal.Body>

        {/* FOOTER */}

        <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{ fontSize: labelFont }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveProcedure}
            style={{
              backgroundColor: "#004aad",
              borderColor: "#004aad",
              color: "white",
              fontSize: labelFont,
            }}
            disabled={
              !selectedProcedure.value || !teeth || !shade || isSubmitting
            }
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TOOTH SELECTOR */}

      <ToothSelectorModal
        show={showToothSelectorModal}
        handleClose={() => setShowToothSelectorModal(false)}
        currentTeeth={teeth}
        onSave={handleSaveTeeth}
      />

      {/* PROCEDURE SELECTOR */}

      <ProcedureSelectorModal
        show={showProcedureSelectorModal}
        handleClose={() => setShowProcedureSelectorModal(false)}
        onSelect={handleProcedureSelect}
        allProcedures={procedureOptions}
        accountId={caseAccountId}
      />
    </>
  );
};

export default ProcedureModel;